import React, {useEffect, useState} from 'react';
import classNames from 'classnames';
import Chart from 'chart.js';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import {BOGetCreatedContestsResponse} from '@fantaleague/mpp_types';
import numeral from 'numeral';
import {getIncrease} from '../../../../../../helpers/stats';
import {getMPPEndpoint, getHeaders} from '../../../../../../helpers/endpoint';
import Loader from '../../../../../../components/Loader';
import DateRangePickerWrapper from '../../../../../../components/DatePicker/DateRangePickerWrapper';

let chart;

const CreatedContests = () => {
  const dispatch = useDispatch();

  const [lang, setLang] = useState('fr-FR');
  const [startDate, setStartDate] = useState(
    moment(new Date().setDate(new Date().getDate() - 30)).format('YYYY-MM-DD'),
  );
  const [endDate, setEndDate] = useState(
    moment(new Date().setDate(new Date().getDate())).format('YYYY-MM-DD'),
  );
  const [leagueStats, setLeagueStats] = useState<
    BOGetCreatedContestsResponse | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const getCreatedContests = () => {
    return (_dispatch, getState) => {
      setIsLoading(true);
      fetch(
        getMPPEndpoint('getCreatedContests', {
          contestType: 'league',
          payload: {startDate: startDate, endDate: endDate},
        }),
        {
          method: 'GET',
          headers: getHeaders(getState().authentication),
        },
      )
        .then(response => response.json())
        .then(leagueData => {
          setLeagueStats(leagueData);
        })
        .catch(() => {})
        .finally(() => {
          setIsLoading(false);
        });
    };
  };

  const renderRow = (
    name: string,
    current: number,
    previous: number,
    currentPercent: number,
  ) => {
    const infinity = previous === 0 && current !== 0;
    const increaseRate = getIncrease(current, previous) || 0;

    return (
      <tr key={name} data-toggle="collapse" data-target={`.${name}`}>
        <td style={{width: '150px '}}>{name}</td>
        <td>
          <b>{numeral(current).format('0,0')}</b>
        </td>
        <td style={{width: '150px '}} className="text-center">
          {numeral(currentPercent || 0).format('0')} %
        </td>
        <td style={{width: '150px '}} className="text-center">
          <b>{numeral(previous).format('0,0')}</b>
        </td>
        <td
          style={{width: '150px '}}
          className={classNames('text-center', {
            'text-green': current > previous,
            'text-red': previous > current,
          })}>
          {infinity ? (
            <span>
              +<span style={{fontSize: '18px'}}>∞</span>
            </span>
          ) : (
            numeral(increaseRate).format('+0')
          )}
          %
        </td>
      </tr>
    );
  };

  const renderCreatedLeagues = () => {
    if (!leagueStats) {
      return null;
    }
    return (
      <div className="mt-3 table-responsive">
        <table className="table table-striped">
          <thead>
            <tr className="bg-gray">
              <th>Ligue</th>
              <th>Nombre</th>
              <th className="text-center">%</th>
              <th className="text-center">vs S-1</th>
              <th className="text-center">% S-1</th>
            </tr>
          </thead>
          <tbody>
            {renderRow(
              'Classique',
              leagueStats.classic.current.totalContests,
              leagueStats.classic.previous.totalContests,
              leagueStats.classic.current.percentage,
            )}
            {renderRow(
              '1vs1',
              leagueStats?.['1v1'].current.totalContests,
              leagueStats?.['1v1'].previous.totalContests,
              leagueStats?.['1v1'].current.percentage,
            )}
          </tbody>
        </table>
      </div>
    );
  };

  useEffect(() => {
    const ctx = document.getElementById('leagues-stats-chart');
    if (ctx) {
      chart = new Chart(ctx, {
        type: 'bar',
        data: {datasets: []},
        options: {
          scales: {
            xAxes: [
              {
                id: 'date',
                type: 'time',
                stacked: true,
              },
            ],
            yAxes: [
              {
                id: 'members',
                type: 'linear',
                stacked: true,
              },
            ],
          },
        },
      });
    }
  }, []);

  const updateGraph = (data: BOGetCreatedContestsResponse) => {
    const numberOfDays = moment(endDate).diff(moment(startDate), 'days');
    const classicLeaguesData = [...Array(numberOfDays + 1)].map((el, index) => {
      const date = moment(startDate).add(index, 'days').format('YYYY-MM-DD');
      return {
        date: moment(date).format('LL'),
        value: data?.classic.current.graph[date] || 0,
      };
    });
    const versusLeaguesData = [...Array(numberOfDays + 1)].map((el, index) => {
      const date = moment(startDate).add(index, 'days').format('YYYY-MM-DD');
      return {
        date: moment(date).format('LL'),
        value: data?.['1v1'].current.graph[date] || 0,
      };
    });

    const datasets = [
      {
        label: 'Classique',
        backgroundColor: '#0d88e6',
        xAxisID: 'date',
        yAxisID: 'members',
        data: [],
      },
      {
        label: '1vs1',
        backgroundColor: '#8be04e',
        xAxisID: 'date',
        yAxisID: 'members',
        data: [],
      },
    ];

    classicLeaguesData.forEach(element => {
      // @ts-ignore
      datasets[0].data.push({
        x: element.date,
        y: element.value,
      });
    });

    versusLeaguesData.forEach(element => {
      // @ts-ignore
      datasets[1].data.push({
        x: element.date,
        y: element.value,
      });
    });

    chart.data.datasets = datasets;
    chart.update();
  };

  useEffect(() => {
    if (leagueStats) {
      updateGraph(leagueStats);
    }
  }, [leagueStats]);

  return (
    <div className="leagues-stats-widget">
      <div className="box">
        <div className="box-header with-border">
          <h3 className="box-title pull-left">
            <i className="fa fa-trophy" /> Ligue créées
          </h3>
          <br />
          Nombres de ligues crées entre le {startDate} et le {endDate}
        </div>
        <div className="box-body">
          <div className="text-center">
            <div className="inline lang">
              <button
                className={classNames('btn btn-box-tool', {
                  selected: lang === 'fr-FR',
                })}
                onClick={() => setLang('fr-FR')}>
                <span className="flag-icon flag-icon-fr" />
              </button>
            </div>

            <div className="period-filter inline data-range-picker">
              <DateRangePickerWrapper
                initialStartDate={moment(startDate)}
                initialEndDate={moment(endDate)}
                minimumNights={0}
                onDatesChange={({startDate, endDate}) => {
                  setStartDate(moment(startDate).format('YYYY-MM-DD'));
                  setEndDate(moment(endDate).format('YYYY-MM-DD'));
                }}
              />
              <button
                className="mb-1 inline btn btn-default btn-flat"
                onClick={() => dispatch(getCreatedContests())}>
                <i className="fa fa-refresh" />
              </button>
            </div>
          </div>
        </div>
        {isLoading && <Loader />}
        {renderCreatedLeagues()}
        <div
          className={classNames(
            'chart',
            leagueStats ? 'displayGraphs' : 'notDisplayGraphs',
          )}>
          <canvas id="leagues-stats-chart" />
        </div>
      </div>
    </div>
  );
};

export default CreatedContests;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/Stats/components/CreatedContests/index.tsx